import React from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import { lighten, styled } from '@mui/material/styles';
import { useTranslation } from '@askporter/config-provider';
import { Typography } from '../Typography';

export interface WebchatMessageProps {
  /**
   * The message text content (optional if children provided)
   */
  text?: string;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Optional timestamp for the message
   */
  timestamp?: string;
  /**
   * Avatar URL for the sender (optional)
   */
  avatarUrl?: string;
  /**
   * Avatar initials for the sender (optional)
   */
  avatarInitials?: string;
  /**
   * Optional children to render instead of text
   */
  children?: React.ReactNode;
  /**
   * Whether to remove the minimum width of the message bubble
   */
  removeMinWidth?: boolean;

  /**
   * Whether to remove the maximum width of the message bubble
   */
  removeMaxWidth?: boolean;
}

export const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sender',
})<{ sender: 'bot' | 'user' }>(({ theme, sender }) => ({
  display: 'flex',
  flexDirection: sender === 'user' ? 'row-reverse' : 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
}));

const Avatar = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[300],
  flexShrink: 0,
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  overflow: 'hidden',
}));

export const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sender' && prop !== 'removeMinWidth' && prop !== 'removeMaxWidth',
})<{ sender: 'bot' | 'user'; removeMinWidth?: boolean; removeMaxWidth?: boolean }>(
  ({ theme, sender, removeMinWidth, removeMaxWidth }) => ({
    ...(removeMaxWidth ? {} : { maxWidth: 480 }),
    minWidth: removeMinWidth ? 'auto' : 250,
    padding: removeMaxWidth ? 0 : '8px 12px',
    borderRadius: removeMaxWidth ? 0 : sender === 'user' ? '8px 0 8px 8px' : '0 8px 8px 8px',
    border: removeMaxWidth
      ? 'none'
      : sender === 'user'
      ? `1px solid ${theme.palette.primary.main}`
      : '1px solid #E9EAEB',
    backgroundColor: removeMaxWidth
      ? 'transparent'
      : sender === 'user'
      ? lighten(theme.palette.primary.main, 0.9)
      : '#F5F5F5',
    color: '#181D27',
    wordBreak: 'break-word',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    overflow: removeMaxWidth ? 'visible' : 'hidden',
  }),
);

const MessageText = styled(Typography)(() => ({
  fontSize: '16px',
  lineHeight: '20px',
  margin: 0,
  whiteSpace: 'pre-wrap',
}));

const TimestampText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'sender',
})<{ sender: 'bot' | 'user' }>(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.grey[500],
  marginBottom: 0,
  textAlign: 'right',
}));

/**
 * WebchatMessage displays a message bubble with sender information
 * and timestamp. It supports both bot and user messages with different
 * styling based on the role. Can display either text or custom children.
 */
export const WebchatMessage: React.FC<WebchatMessageProps> = ({
  text,
  sender,
  timestamp,
  avatarUrl,
  avatarInitials,
  children,
  removeMinWidth = false,
  removeMaxWidth = false,
}) => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <MessageContainer sender={sender} sx={removeMaxWidth ? { overflow: 'visible' } : undefined}>
      {sender === 'bot' && (
        <Avatar sx={{ mt: 1 }}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={`${sender} avatar`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500, fontSize: '14px' }}>
              {avatarInitials || (sender === 'bot' ? 'B' : 'U')}
            </Typography>
          )}
        </Avatar>
      )}
      <Box
        ml={sender === 'bot' ? 2 : 0}
        sx={
          removeMaxWidth
            ? { overflow: 'visible', flex: 1, width: '100%', maxWidth: '100%' }
            : { maxWidth: isSmallScreen ? '80%' : '480px' }
        }
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent={sender === 'bot' ? 'space-between' : 'flex-end'}
        >
          {sender === 'bot' && (
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500, fontSize: '14px' }}>
              {t('webchat:assistant_name')}
            </Typography>
          )}
          {timestamp && (
            <Box sx={removeMaxWidth ? { marginRight: '42px' } : undefined}>
              {/**
               * The above is a hack to align the timestamp with the message bubble for carousel messages
               */}
              <TimestampText sender={sender}>{timestamp}</TimestampText>
            </Box>
          )}
        </Box>
        <Box mt={'6px'} sx={removeMaxWidth ? { overflow: 'visible' } : undefined}>
          <MessageBubble sender={sender} removeMinWidth={removeMinWidth} removeMaxWidth={removeMaxWidth}>
            {children ? children : <MessageText>{text}</MessageText>}
          </MessageBubble>
        </Box>
      </Box>
    </MessageContainer>
  );
};
